import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { davidApiService, ChatResponse } from '../services/davidApi';
import { projectApiService, Project } from '../../recruiter/services/projectApi';
import { COMPANY_ROUTES } from '../constants';
import {
  badgeForRank,
  shouldShowBars,
  barWidthPercent,
} from '../utils/candidateMatchPresentation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Search,
  Send,
  Paperclip,
  ArrowUpRight,
  ArrowLeft,
  FileText,
  Users,
  BarChart2,
  Folder,
  Plus
} from 'lucide-react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'david';
  timestamp: Date;
  isTyping?: boolean;
  attachment?: {
    name: string;
    size: number;
    type: string;
  };
  // v2.1 — UI payload attached by backend tool handlers
  uiPayload?: {
    navigate_to?: string | null;
    suggested_actions?: Array<{ label: string; intent: string }>;
    highlights?: Record<string, any>;
  };
}

/**
 * Talk to David - AI Recruiter Assistant for Companies
 * Connects to david-recruiter service
 */
const TalkToDavid: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  // Slice 4.4a — Strict Mode mounts the component twice in dev, which would
  // call useState's initializer twice and produce TWO different eager session
  // ids — one of which gets stuck in localStorage as a ghost (a session_id
  // that was never sent to /start, leading to 404s on the next refresh).
  // Pin the eager id in sessionStorage so both Strict Mode mounts read the
  // same value, eliminating the dual-mount race.
  const [chatSessionId, setChatSessionId] = useState(() => {
    if (typeof window === 'undefined') return davidApiService.generateSessionId();
    const cached = window.sessionStorage.getItem('david:tabEagerId');
    if (cached) return cached;
    const fresh = davidApiService.generateSessionId();
    window.sessionStorage.setItem('david:tabEagerId', fresh);
    return fresh;
  });
  // Slice 4.4a — guard so the rehydrate-on-mount effect runs exactly once per
  // recruiter (after companyData.id resolves). Subsequent setChatSessionId
  // calls (e.g. "Start new chat") write through to localStorage via a
  // separate mirror effect.
  const [didRehydrateSession, setDidRehydrateSession] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Slice 4.9 — Past Chats / read-only viewing.
  // Plan A: when ?sessionId=<id> is on the URL (set by clicking a row in
  // the sidebar's Past chats expansion), TalkToDavid loads that session
  // visually and disables all interactive elements (input, picker buttons,
  // candidate cards, chips). The recruiter clicks "Start new chat" to leave.
  //
  // TODO Plan C — when we want "continue this conversation": mint a NEW
  // session_id seeded with the past session's selected_job_id /
  // selected_candidate_id, leaving the original frozen. Sidesteps the
  // stale-data class of bugs entirely (old last_shown_* doesn't bleed
  // into a live chat).
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [companyData, setCompanyData] = useState({ id: '', name: '' });
  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [userRole, setUserRole] = useState<string | undefined>(undefined);  // 'company', 'recruiter', 'candidate'
  const [actualCompanyId, setActualCompanyId] = useState<string | undefined>(undefined);  // From companyData localStorage

  // Project selection state
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [showProjectSelection, setShowProjectSelection] = useState(false);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [pendingFeature, setPendingFeature] = useState<string | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);  // Job ID for linking to project
  const [awaitingProjectForJobCreation, setAwaitingProjectForJobCreation] = useState(false);  // True when we need to create job after project selection
  const [completedJobId, setCompletedJobId] = useState<string | null>(null);  // Job ID after successful posting (for review card)

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formInitializedRef = useRef<boolean>(false); // Guard against double initialization in React Strict Mode

  // Load company data from localStorage and handle navigation state (projectId, formData)
  useEffect(() => {
    const locationState = location.state as any;

    // Get projectId from navigation state (if coming from JobPostingMethod or JobPostingForm)
    if (locationState?.projectId) {
      setProjectId(locationState.projectId);
      console.log('[TalkToDavid] Received projectId from navigation:', locationState.projectId);
    }

    const userDataString = localStorage.getItem('authUser');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        const recruiterId = userData.id; // This is the user/recruiter ID
        const companyName = userData.companyName || userData.firstName || 'Company';
        setCompanyData({ id: recruiterId, name: companyName });

        // Get actual company ID from companyData localStorage (separate from authUser)
        const companyDataString = localStorage.getItem('companyData');
        let companyIdFromStorage: string | undefined;
        if (companyDataString) {
          try {
            const companyDataObj = JSON.parse(companyDataString);
            companyIdFromStorage = companyDataObj.id;
            console.log('[TalkToDavid] Found company ID from companyData:', companyIdFromStorage);
          } catch (e) {
            console.error('[TalkToDavid] Failed to parse companyData:', e);
          }
        }

        // Store userRole and companyId in state for use in PDF upload and other flows
        const role = userData.userType;  // 'company', 'recruiter', 'candidate'
        setUserRole(role);
        setActualCompanyId(companyIdFromStorage);
        console.log('[TalkToDavid] Stored userRole:', role, 'companyId:', companyIdFromStorage);

        // If coming from JobPostingForm with form data, initialize chat with LLM processing
        // Guard against React Strict Mode double-invocation
        if (locationState?.fromForm && locationState?.formData && !formInitializedRef.current) {
          formInitializedRef.current = true; // Mark as initialized
          console.log('[TalkToDavid] Detected form data from JobPostingForm:', locationState.formData);
          console.log('[TalkToDavid] User role from localStorage:', role);
          console.log('[TalkToDavid] Company ID to send:', companyIdFromStorage);
          console.log('[TalkToDavid] Session ID:', chatSessionId);
          initializeChatFromForm(
            chatSessionId,
            recruiterId,
            locationState.formData,
            locationState.projectId,
            role,
            companyIdFromStorage
          );
        }
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }
  }, [location.state]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Slice 4.4a — durable session_id + restore-on-mount.
   *
   * Resolution order on first valid render (companyData.id available):
   *   1. URL param `?sessionId=` (deep-linking from Past Chats etc.)
   *   2. localStorage `david:lastSession:{recruiterId}` (the previous session
   *      this browser was last using)
   *   3. fallback: keep the eager-generated id from useState init
   *
   * If a candidate id is found, we fetch GET /v2/chat/session/{id}. On 200 we
   * swap chatSessionId to it and replay the messages. On 404 (stale id) we
   * silently keep the eager id and overwrite localStorage.
   *
   * Runs at most once per recruiter (didRehydrateSession guards). A separate
   * mirror effect below keeps localStorage in sync with chatSessionId so
   * "Start new chat" / fresh ids automatically persist.
   */
  useEffect(() => {
    if (didRehydrateSession) return;
    if (!companyData.id) return;
    setDidRehydrateSession(true);

    const lsKey = `david:lastSession:${companyData.id}`;
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('sessionId');
    const fromLs = localStorage.getItem(lsKey);
    const candidate = fromUrl || fromLs;

    if (!candidate) {
      // No prior session for this browser — persist the eager-generated id
      // so the NEXT reload can find it.
      localStorage.setItem(lsKey, chatSessionId);
      return;
    }

    davidApiService.getSession(candidate).then((session) => {
      if (!session) {
        // Stale localStorage id (server has no record). Drop it and keep
        // the eager fresh id.
        localStorage.setItem(lsKey, chatSessionId);
        return;
      }
      // Valid persisted session. Switch to its id and rebuild the chat.
      setChatSessionId(candidate);
      localStorage.setItem(lsKey, candidate);

      if (session.messages.length === 0) {
        // Empty thread (e.g. /start ran but no /message yet) — leave the
        // welcome screen visible so the user can re-enter the flow.
        return;
      }

      // Each persisted message can carry its own ui_payload — that's how
      // cards rendered three turns ago survive a refresh even when the
      // current turn is a no-tool general_chat.
      const restored: Message[] = session.messages.map((m) => ({
        id: m.id,
        text: m.content,
        sender: m.role === 'user' ? 'user' : 'david',
        timestamp: new Date(m.timestamp),
        uiPayload: m.ui_payload || undefined,
      }));
      setMessages(restored);
      setShowWelcome(false);
    }).catch((err) => {
      console.error('[TalkToDavid] session rehydrate failed:', err);
      // Don't block the UI — keep eager id, user can start fresh.
      localStorage.setItem(lsKey, chatSessionId);
    });
  }, [companyData.id, didRehydrateSession, chatSessionId, location.search]);

  // Slice 4.4a — keep localStorage mirror in sync with the live session id.
  // Skipped during the initial mount pass (the rehydrate effect above owns
  // that write). Fires on every subsequent change, including the
  // "Start new chat" flow that calls setChatSessionId(generateSessionId()).
  useEffect(() => {
    if (!didRehydrateSession) return;
    if (!companyData.id || !chatSessionId) return;
    localStorage.setItem(`david:lastSession:${companyData.id}`, chatSessionId);
  }, [chatSessionId, companyData.id, didRehydrateSession]);

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoadingProjects(true);
        const response = await projectApiService.listProjects(1, 100);
        setProjects(response.data || []);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Handle creating a new project
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    try {
      setIsCreatingProject(true);
      const newProject = await projectApiService.createProject(newProjectName.trim());

      // Add to projects list
      setProjects(prev => [...prev, newProject]);
      const newProjectId = newProject.projectId || newProject._id;
      setProjectId(newProjectId);

      // Close modal and reset
      setShowCreateProjectModal(false);
      setNewProjectName('');
      setShowProjectSelection(false);

      // NEW FLOW: If awaiting project for job creation, create job with new project
      if (awaitingProjectForJobCreation && newProjectId) {
        try {
          setIsLoading(true);
          console.log('[TalkToDavid] Creating job with new project:', newProjectId);

          // Call new endpoint to create job with project
          const result = await davidApiService.completeWithProject(chatSessionId, newProjectId);

          if (result.success) {
            // Add completion message
            setMessages(prev => [...prev, {
              id: `david-${Date.now()}`,
              text: `I've created a new folder called **"${newProject.name}"** and saved your job there.\n\nYour job posting is now complete! 🎉 Please review your posted job to make sure everything looks perfect.`,
              sender: 'david',
              timestamp: new Date()
            }]);

            // Store job ID for review card
            if (result.job_id) setCompletedJobId(result.job_id);
            // Mark conversation as complete
            setIsCompleted(true);
          } else {
            throw new Error(result.message || 'Failed to create job');
          }

          setAwaitingProjectForJobCreation(false);
        } catch (error) {
          console.error('Failed to create job with new project:', error);
          setMessages(prev => [...prev, {
            id: `david-${Date.now()}`,
            text: 'I created the folder but had trouble creating your job posting. Please try again or contact support.',
            sender: 'david',
            timestamp: new Date()
          }]);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // LEGACY FLOW: If there's a currentJobId, link existing job to new project
      if (currentJobId && newProjectId) {
        try {
          setIsLoading(true);
          console.log('[TalkToDavid] Linking existing job', currentJobId, 'to new project', newProjectId);

          // Link job to the new project via job-service
          await projectApiService.addJobToProject(newProjectId, currentJobId);

          // Add completion message
          setMessages(prev => [...prev, {
            id: `david-${Date.now()}`,
            text: `I've created a new folder called **"${newProject.name}"** and saved your job there.\n\nYour job posting is now complete! 🎉 Please review your posted job to make sure everything looks perfect.`,
            sender: 'david',
            timestamp: new Date()
          }]);

          // Store job ID for review card
          setCompletedJobId(currentJobId);
          // Mark conversation as complete
          setIsCompleted(true);
          setCurrentJobId(null);
        } catch (error) {
          console.error('Failed to link job to new project:', error);
          setMessages(prev => [...prev, {
            id: `david-${Date.now()}`,
            text: 'I created the folder but had trouble saving your job to it. Please try again or contact support.',
            sender: 'david',
            timestamp: new Date()
          }]);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // If there's a pending feature, continue with it now that project is created
      if (pendingFeature) {
        const feature = pendingFeature;
        setPendingFeature(null);

        // Add confirmation message
        setMessages([{
          id: `david-${Date.now()}`,
          text: `Great! I've created a new folder called "${newProject.name}" and your job will be saved there. Let me help you create a job posting.`,
          sender: 'david',
          timestamp: new Date()
        }]);

        // Now start the chat with the new project
        try {
          setIsLoading(true);
          const recruiterId = companyData.id;
          const response: ChatResponse = await davidApiService.startChat(chatSessionId, recruiterId, undefined, newProjectId);

          const davidMessage: Message = {
            id: `david-${Date.now()}`,
            text: response.message,
            sender: 'david',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, davidMessage]);
        } catch (error) {
          console.error('Failed to start chat:', error);
          setMessages(prev => [...prev, {
            id: `david-${Date.now()}`,
            text: 'Sorry, I encountered an error starting the conversation. Please try again.',
            sender: 'david',
            timestamp: new Date()
          }]);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Regular project creation during conversation
        setMessages(prev => [...prev, {
          id: `david-${Date.now()}`,
          text: `Great! I've created a new folder called "${newProject.name}" and your job will be saved there.`,
          sender: 'david',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project. Please try again.');
    } finally {
      setIsCreatingProject(false);
    }
  };

  // Handle project selection
  const handleProjectSelect = async (selectedId: string | undefined) => {
    setProjectId(selectedId);
    const projectName = selectedId
      ? projects.find(p => (p._id || p.projectId) === selectedId)?.name || 'Selected Project'
      : 'Default';

    setShowProjectSelection(false);

    // NEW FLOW: If awaiting project for job creation, create job with selected project
    // This happens when all fields are collected but no job exists yet
    if (awaitingProjectForJobCreation && selectedId) {
      try {
        setIsLoading(true);
        console.log('[TalkToDavid] Creating job with project:', selectedId);

        // Call new endpoint to create job with project
        const result = await davidApiService.completeWithProject(chatSessionId, selectedId);

        if (result.success) {
          // Add completion message
          setMessages(prev => [...prev, {
            id: `david-${Date.now()}`,
            text: `Your job has been saved to the **"${projectName}"** folder.\n\nYour job posting is now complete! 🎉 Please review your posted job to make sure everything looks perfect.`,
            sender: 'david',
            timestamp: new Date()
          }]);

          // Store job ID for review card
          if (result.job_id) setCompletedJobId(result.job_id);
          // Mark conversation as complete
          setIsCompleted(true);
        } else {
          throw new Error(result.message || 'Failed to create job');
        }

        setAwaitingProjectForJobCreation(false);
      } catch (error) {
        console.error('Failed to create job with project:', error);
        setMessages(prev => [...prev, {
          id: `david-${Date.now()}`,
          text: 'Sorry, I had trouble creating your job posting. Please try again or contact support.',
          sender: 'david',
          timestamp: new Date()
        }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // LEGACY FLOW: If there's a currentJobId, link existing job to project
    // (This handles cases where job was already created)
    if (currentJobId && selectedId) {
      try {
        setIsLoading(true);
        console.log('[TalkToDavid] Linking existing job', currentJobId, 'to project', selectedId);

        // Link job to project via job-service
        await projectApiService.addJobToProject(selectedId, currentJobId);

        // Add completion message
        setMessages(prev => [...prev, {
          id: `david-${Date.now()}`,
          text: `Your job has been saved to the **"${projectName}"** folder.\n\nYour job posting is now complete! 🎉 Please review your posted job to make sure everything looks perfect.`,
          sender: 'david',
          timestamp: new Date()
        }]);

        // Store job ID for review card
        setCompletedJobId(currentJobId);
        // Mark conversation as complete
        setIsCompleted(true);
        setCurrentJobId(null);
      } catch (error) {
        console.error('Failed to link job to project:', error);
        setMessages(prev => [...prev, {
          id: `david-${Date.now()}`,
          text: 'Sorry, I had trouble saving your job to the folder. Please try again or contact support.',
          sender: 'david',
          timestamp: new Date()
        }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // If there's a pending feature, continue with it now that project is selected
    if (pendingFeature) {
      const feature = pendingFeature;
      setPendingFeature(null);

      // Add confirmation message
      setMessages([{
        id: `david-${Date.now()}`,
        text: `Great! Your job will be saved to the "${projectName}" folder. Let me help you create a job posting.`,
        sender: 'david',
        timestamp: new Date()
      }]);

      // Now start the chat with the selected project
      try {
        setIsLoading(true);
        const recruiterId = companyData.id;
        const response: ChatResponse = await davidApiService.startChat(chatSessionId, recruiterId, undefined, selectedId);

        const davidMessage: Message = {
          id: `david-${Date.now()}`,
          text: response.message,
          sender: 'david',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, davidMessage]);
      } catch (error) {
        console.error('Failed to start chat:', error);
        setMessages(prev => [...prev, {
          id: `david-${Date.now()}`,
          text: 'Sorry, I encountered an error starting the conversation. Please try again.',
          sender: 'david',
          timestamp: new Date()
        }]);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Regular project selection during conversation
      setMessages(prev => [...prev, {
        id: `david-${Date.now()}`,
        text: `Perfect! Your job will be saved to the "${projectName}" folder.`,
        sender: 'david',
        timestamp: new Date()
      }]);
    }
  };

  const initializeChat = async () => {
    try {
      setIsLoading(true);

      const recruiterId = companyData.id;

      if (!recruiterId) {
        console.error('[TalkToDavid] No recruiter ID found');
        throw new Error('Company ID not found. Please log in again.');
      }

      console.log('[TalkToDavid] Starting chat with recruiter ID:', recruiterId, 'projectId:', projectId);

      const response: ChatResponse = await davidApiService.startChat(chatSessionId, recruiterId, undefined, projectId);

      const davidMessage: Message = {
        id: `david-${Date.now()}`,
        text: response.message,
        sender: 'david',
        timestamp: new Date()
      };

      setMessages([davidMessage]);
    } catch (error) {
      console.error('Failed to start chat:', error);
      const fallbackMessage: Message = {
        id: `david-${Date.now()}`,
        text: 'Hi! I\'m David, your AI recruitment assistant.\n\nUpload a job description PDF using the attachment icon, or type your job details here — I\'ll review it and ask about anything that\'s missing.',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Slice 4.9 — load a past session into the chat in READ-ONLY mode.
   *
   * Plan A: the conversation restores visually but every interactive element
   * (input, picker buttons, candidate cards, suggested-action chips) is
   * disabled by isReadOnly. Recruiter clicks "Start new chat" to leave.
   *
   * Triggered by the URL param `?sessionId=<id>` set when a row in the
   * sidebar's Past chats expansion is clicked.
   *
   * TODO Plan C — when ready to support "continue this conversation":
   * mint a new session_id, copy the past session's selected_job_id /
   * selected_candidate_id into it as starting state, leave the original
   * session frozen in Past Chats. That sidesteps the stale-data class of
   * bugs entirely (old last_shown_* doesn't bleed into a live chat).
   */
  const openPastSession = async (sessionId: string) => {
    if (!sessionId) return;
    try {
      setIsLoading(true);
      const session = await davidApiService.getSession(sessionId);
      if (!session) {
        console.warn('[TalkToDavid] past session not found:', sessionId);
        return;
      }
      const restored: Message[] = session.messages.map((m) => ({
        id: m.id,
        text: m.content,
        sender: m.role === 'user' ? 'user' : 'david',
        timestamp: new Date(m.timestamp),
        uiPayload: m.ui_payload || undefined,
      }));
      setChatSessionId(sessionId);
      setMessages(restored);
      setShowWelcome(false);
      setIsReadOnly(true);
    } catch (e) {
      console.error('[TalkToDavid] failed to open past session:', e);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Slice 4.9 — leave read-only mode and start a fresh conversation.
   * Generates a brand-new chatSessionId, clears the screen, re-shows the
   * welcome cards, and strips ?sessionId= from the URL so a refresh
   * doesn't re-enter read-only mode. The mirror effect keeps localStorage
   * in sync with the new id.
   */
  const startNewChat = () => {
    const fresh = davidApiService.generateSessionId();
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('david:tabEagerId', fresh);
    }
    setChatSessionId(fresh);
    setMessages([]);
    setIsReadOnly(false);
    setShowWelcome(true);
    // Remove ?sessionId from the URL so a refresh starts fresh.
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  };

  /**
   * Slice 4.9 — react to the ?sessionId= URL param. The sidebar's Past
   * chats expansion sets it via <Link to="/companies/talk-to-david?sessionId=...">.
   * When it appears (and isn't already what we're viewing), open the
   * session in read-only mode.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('sessionId');
    if (!fromUrl) return;
    if (fromUrl === chatSessionId && isReadOnly) return;  // already viewing this one
    openPastSession(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /**
   * Initialize chat from form data - uses LLM processing like PDF upload flow
   * Called when navigating from JobPostingForm with form data
   */
  const initializeChatFromForm = async (
    sessionId: string,
    recruiterId: string,
    formData: Record<string, any>,
    formProjectId?: string,
    userRole?: string,
    companyIdFromStorage?: string
  ) => {
    try {
      setIsLoading(true);
      setShowWelcome(false); // Skip welcome screen

      if (!recruiterId) {
        console.error('[TalkToDavid] No recruiter ID found');
        throw new Error('Company ID not found. Please log in again.');
      }

      console.log('[TalkToDavid] Starting chat from form with LLM processing');
      console.log('[TalkToDavid] Session ID:', sessionId);
      console.log('[TalkToDavid] Form data:', formData);
      console.log('[TalkToDavid] Project ID:', formProjectId);

      // Call the new LLM-powered endpoint (passing sessionId like other flows)
      const response: ChatResponse = await davidApiService.startFromFormWithLLM(
        sessionId,
        formData,
        recruiterId,
        formProjectId,
        userRole,
        companyIdFromStorage || undefined
      );

      // David's response contains introduction + first question (if any missing fields)
      const davidMessage: Message = {
        id: `david-${Date.now()}`,
        text: response.message,
        sender: 'david',
        timestamp: new Date()
      };

      setMessages([davidMessage]);

      // Check if we need project selection or if conversation is complete
      if (response.requiresProjectSelection) {
        console.log('[TalkToDavid] Form flow: Project selection required');
        if (response.jobId) {
          // Legacy flow: job already exists, just need to link to project
          setCurrentJobId(response.jobId);
        } else {
          // New flow: no job yet, will create after project selection
          setAwaitingProjectForJobCreation(true);
        }
        setShowProjectSelection(true);
      } else if (response.completed) {
        if (response.jobId) setCompletedJobId(response.jobId);
        setIsCompleted(true);
      }

    } catch (error: any) {
      console.error('Failed to start chat from form:', error);
      const errorMessage: Message = {
        id: `david-${Date.now()}`,
        text: error.response?.data?.detail ||
          'Sorry, I encountered an error processing your form data. Please try again or contact support.',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([errorMessage]);
    } finally {
      setIsLoading(false);
      textareaRef.current?.focus();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      console.log('File selected:', file.name, file.size, file.type);
    }
  };

  /**
   * v2.1 — user tapped a suggested-action chip under David's reply.
   * Chips are deterministic: we pass the chip's intent explicitly so the backend
   * skips LLM classification. The chip label goes in as the message text (for
   * conversation history) but we do NOT render a user bubble — chips shouldn't
   * clutter the chat with repetitive "Refine job description" echoes.
   */
  const handleSuggestedAction = async (label: string, intent: string) => {
    // Slice 4.9 — Plan A: past chats are read-only.
    if (isReadOnly) return;
    if (isLoading || isCompleted) return;
    try {
      setIsLoading(true);
      await handleRegularChat(label, undefined, intent);
    } catch (error: any) {
      console.error('Failed to run suggested action:', error);
      setMessages(prev => [...prev, {
        id: `david-${Date.now()}`,
        text: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        sender: 'david',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * v2.1 — user picked a job from the dropdown David rendered in
   * ui_payload.highlights.jobs. We send the title as the user message, the
   * job id explicitly, AND forced_intent=find_candidates_for_role so the
   * backend skips both the fuzzy resolver and the LLM classifier.
   */
  const handleJobSelect = async (jobId: string, title: string) => {
    if (isReadOnly) return;
    if (isLoading || isCompleted) return;
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: title,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    try {
      setIsLoading(true);
      await handleRegularChat(title, jobId, 'find_candidates_for_role');
    } catch (error: any) {
      console.error('Failed to select job:', error);
      setMessages(prev => [...prev, {
        id: `david-${Date.now()}`,
        text: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        sender: 'david',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Slice 3 — user clicked a candidate card under a David reply that listed
   * applicants. We send a follow-up "Tell me about <name>" message; the
   * graph's classify_intent will route it (general_chat in Slice 3, or
   * summarize_applicant once Slice 6 ships). The candidate id is rendered
   * into the prompt so the LLM has a concrete reference even before the
   * dedicated summarize tool exists.
   */
  const handleCandidateSelect = async (candidateId: string, name: string) => {
    if (isReadOnly) return;
    if (isLoading || isCompleted) return;
    const followUp = `Tell me more about ${name}`;
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: followUp,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    try {
      setIsLoading(true);
      await handleRegularChat(followUp);
    } catch (error: any) {
      console.error('Failed to select candidate:', error);
      setMessages(prev => [...prev, {
        id: `david-${Date.now()}`,
        text: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        sender: 'david',
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (isReadOnly) return;
    if ((inputMessage.trim() === '' && !selectedFile) || isLoading || isCompleted) return;

    if (showWelcome) {
      setInputMessage('');
      await handleGetStarted();
      return;
    }

    const userMessageText = inputMessage.trim();
    const fileToUpload = selectedFile;
    setInputMessage('');
    setSelectedFile(null);

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: userMessageText || (fileToUpload ? `[Uploaded: ${fileToUpload.name}]` : ''),
      sender: 'user',
      timestamp: new Date(),
      ...(fileToUpload && {
        attachment: {
          name: fileToUpload.name,
          size: fileToUpload.size,
          type: fileToUpload.type
        }
      })
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      setIsLoading(true);

      const recruiterId = companyData.id;
      if (!recruiterId) {
        throw new Error('Company ID not found. Please log in again.');
      }

      // If there's a file, upload it instead of sending a text message
      if (fileToUpload) {
        console.log('[TalkToDavid] Uploading file:', fileToUpload.name, 'with projectId:', projectId, 'userRole:', userRole, 'companyId:', actualCompanyId);
        const response = await davidApiService.uploadJobDescription(
          chatSessionId,
          fileToUpload,
          recruiterId,
          projectId,
          userRole,        // Pass user role for company lookup
          actualCompanyId  // Pass company ID from localStorage
        );

        const davidMessage: Message = {
          id: `david-${Date.now()}`,
          text: response.message,
          sender: 'david',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, davidMessage]);

        // Check if upload response indicates project selection is needed
        // This happens when PDF has all info but no project was pre-selected
        if ((response as any).requires_project_selection) {
          console.log('[TalkToDavid] PDF upload: Project selection required');
          setAwaitingProjectForJobCreation(true);
          setShowProjectSelection(true);
        } else if ((response as any).completed) {
          // PDF had all info AND project was pre-selected - mark complete
          if ((response as any).jobId || (response as any).job_id) {
            setCompletedJobId((response as any).jobId || (response as any).job_id);
          }
          setIsCompleted(true);
        }
        // Otherwise David will ask follow-up questions
      } else {
        // Otherwise send as regular text message
        await handleRegularChat(userMessageText);
      }
    } catch (error: any) {
      console.error('Failed to send message:', error);
      const errorMessage: Message = {
        id: `david-${Date.now()}`,
        text: error.response?.data?.detail || 'Sorry, I encountered an error. Please try again.',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegularChat = async (
    userMessageText: string,
    selectedJobId?: string,
    forcedIntent?: string,
  ) => {
    const recruiterId = companyData.id;

    if (!recruiterId) {
      throw new Error('Company ID not found. Please log in again.');
    }

    const response: ChatResponse = await davidApiService.sendMessage(
      chatSessionId,
      userMessageText,
      recruiterId,
      selectedJobId,
      forcedIntent,
    );

    const davidMessage: Message = {
      id: `david-${Date.now()}`,
      text: response.message,
      sender: 'david',
      timestamp: new Date(),
      uiPayload: response.uiPayload,
    };

    setMessages(prev => [...prev, davidMessage]);

    // Check if we need to show project selection (end of conversation flow)
    if (response.requiresProjectSelection) {
      console.log('[TalkToDavid] Project selection required');
      if (response.jobId) {
        // Legacy flow: job already exists, just need to link to project
        setCurrentJobId(response.jobId);
      } else {
        // New flow: no job yet, will create after project selection
        setAwaitingProjectForJobCreation(true);
      }
      setShowProjectSelection(true);
    } else if (response.completed) {
      if (response.jobId) setCompletedJobId(response.jobId);
      setIsCompleted(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleGetStarted = async () => {
    if (!companyData.id) {
      console.error('[TalkToDavid] Company data not loaded yet');
      return;
    }

    setShowWelcome(false);
    await initializeChat();
    textareaRef.current?.focus();
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleFeatureCardClick = async (featureType: string) => {
    if (isReadOnly) return;
    if (!companyData.id) {
      console.error('[TalkToDavid] Company data not loaded yet');
      return;
    }

    // Hide welcome screen and initialize chat
    setShowWelcome(false);

    try {
      setIsLoading(true);

      const recruiterId = companyData.id;
      if (!recruiterId) {
        throw new Error('Company ID not found.');
      }

      // v2.1 — map feature card to v2 entry_point. The backend generates
      // personalised greetings based on company state for find_candidates,
      // and biases the intent classifier for the other flows.
      const entryPointMap: Record<string, import('../services/davidApi').EntryPoint> = {
        'write-job-description': 'create_job',
        'find-candidates': 'find_candidates',
        'summarize-candidate': 'review_applicant',
        'compare-candidates': 'compare_shortlist',
      };
      const entryPoint = entryPointMap[featureType] ?? 'free_chat';

      const response: ChatResponse = await davidApiService.startChat(
        chatSessionId,
        recruiterId,
        undefined,
        projectId,
        entryPoint,
      );

      const davidMessage: Message = {
        id: `david-${Date.now()}`,
        text: response.message,
        sender: 'david',
        timestamp: new Date(),
        uiPayload: response.uiPayload,
      };
      setMessages([davidMessage]);

    } catch (error) {
      console.error('Failed to start feature chat:', error);
      const fallbackMessage: Message = {
        id: `david-${Date.now()}`,
        text: 'Hi! I\'m David, your AI recruitment assistant.\n\nUpload a job description PDF using the attachment icon, or type your job details here — I\'ll review it and ask about anything that\'s missing.',
        sender: 'david',
        timestamp: new Date()
      };
      setMessages([fallbackMessage]);
    } finally {
      setIsLoading(false);
      textareaRef.current?.focus();
    }
  };

  const FeatureCard = ({ color, icon: Icon, title, desc, featureType }: any) => (
    <button
      onClick={() => handleFeatureCardClick(featureType)}
      disabled={!companyData.id}
      className={`flex flex-col justify-between p-6 rounded-3xl h-[200px] w-full text-white transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${color}`}
    >
      <div className="flex flex-col gap-4">
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
          <Icon size={20} className="text-white" />
        </div>
        <h3 className="text-xl font-bold leading-tight text-left">{title}</h3>
      </div>
      <div className="flex justify-between items-end">
        <p className="text-[10px] text-white/80 text-left max-w-[80%]">{desc}</p>
        <ArrowUpRight size={20} />
      </div>
    </button>
  );

  return (
    <CompanyLayout>
      <div className="flex flex-col h-full font-manrope bg-[#F8F9FB]">
        {showWelcome ? (
          /* Welcome Screen */
          <div className="flex flex-col h-[calc(100vh-200px)] animate-fade-in items-center justify-center relative">
            <div className="w-full max-w-5xl mx-auto px-4 flex flex-col items-center justify-center flex-1">
              {/* Hero Text */}
              <div className="relative mb-16 text-center">
                <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 bg-[#CFE3FF] rounded-full blur-3xl opacity-50 -z-10"></div>
                <h1 className="text-5xl md:text-6xl font-bold text-[#015FA6] mb-2 tracking-tight">
                  Hi! My Name Is David,
                </h1>
                <h2 className="text-4xl md:text-5xl font-medium text-[#A5A5A5] tracking-tight">
                  Your AI Recruiting Assistant.
                </h2>
              </div>

              {/* Feature Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 w-full mb-16">
                <FeatureCard
                  color="bg-[#015FA6]"
                  icon={Search}
                  title="Find candidates for a role"
                  desc="Get instant candidate recommendations based on your requirements."
                  featureType="find-candidates"
                />
                <FeatureCard
                  color="bg-[#FBBF24]"
                  icon={FileText}
                  title="Create a Job"
                  desc="Use AI to generate or refine your job posting."
                  featureType="write-job-description"
                />
                <FeatureCard
                  color="bg-[#458330]"
                  icon={Users}
                  title="Review an applicant"
                  desc="Get a structured summary of a candidate who has shared their profile."
                  featureType="summarize-candidate"
                />
                <FeatureCard
                  color="bg-black"
                  icon={BarChart2}
                  title="Compare shortlisted candidates"
                  desc="AI helps rank candidates based on fit for your role."
                  featureType="compare-candidates"
                />
              </div>

              {/* Company Error Message */}
              {!companyData.id && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 max-w-2xl">
                  <p className="font-medium">Authentication Required</p>
                  <p className="text-sm">Please log in to use the AI assistant.</p>
                </div>
              )}
            </div>

            {/* Chat Input Area */}
            <div className="w-full max-w-4xl mx-auto px-4 pb-8">
              <div className="relative bg-white rounded-2xl border border-[#EDEDED] shadow-sm p-4 flex flex-col">
                <textarea
                  ref={textareaRef}
                  data-testid="david-chat-input"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={!companyData.id ? "Please log in first" : "Type your message here..."}
                  disabled={isLoading || !companyData.id}
                  className="w-full min-h-[60px] resize-none outline-none text-base placeholder-[#A5A5A5] text-[#0B111E] disabled:opacity-50"
                />

                {selectedFile && (
                  <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded-lg text-sm text-[#015FA6]">
                    <Paperclip size={16} />
                    <span>{selectedFile.name}</span>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="ml-auto text-gray-500 hover:text-red-500"
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className="flex justify-between items-center mt-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || isCompleted || !companyData.id}
                    className="text-[#A5A5A5] hover:text-[#015FA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Paperclip size={20} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt"
                  />
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="david-send-btn"
                      onClick={showWelcome ? handleGetStarted : handleSendMessage}
                      disabled={isLoading || isCompleted || !companyData.id}
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        (inputMessage.trim() || selectedFile) && !isLoading && !isCompleted && companyData.id
                          ? 'bg-[#015FA6] hover:bg-[#014a85]'
                          : 'bg-[#A5A5A5]'
                      }`}
                    >
                      <Send size={18} className="-ml-0.5 mt-0.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Chat Interface */
          <div className="flex flex-col h-full">
            {/* Chat Header with Back Button */}
            <div className="flex items-center gap-4 px-8 py-4 border-b border-[#EDEDED] bg-white">
              <button
                onClick={() => {
                  setShowWelcome(true);
                  setMessages([]);
                  setIsCompleted(false);
                  setCompletedJobId(null);
                  setCurrentJobId(null);
                  setShowProjectSelection(false);
                  setAwaitingProjectForJobCreation(false);
                  setChatSessionId(davidApiService.generateSessionId());
                }}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Go back to welcome screen"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#015FA6] flex items-center justify-center text-white font-bold">
                  D
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">David</h2>
                  <p className="text-sm text-gray-500">Your AI Recruiting Assistant</p>
                </div>
              </div>
            </div>
            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              <div className="max-w-4xl mx-auto space-y-6">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-2xl ${message.sender === 'user'
                      ? 'bg-[#015FA6] text-white rounded-3xl rounded-br-md px-6 py-4'
                      : 'bg-white text-gray-900 rounded-3xl rounded-bl-md px-6 py-4 border border-[#EDEDED] shadow-sm'
                      }`}>
                      {message.isTyping ? (
                        <div className="flex items-center space-x-1">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      ) : (
                        <>
                          {message.sender === 'david' ? (
                            <div className="prose prose-sm max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-900 prose-p:leading-relaxed prose-strong:text-gray-900 prose-strong:font-bold prose-ul:text-gray-900 prose-ol:text-gray-900 prose-li:text-gray-900 prose-code:text-blue-600 prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50 prose-blockquote:py-1">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.text}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <p className="text-base leading-relaxed">{message.text}</p>
                          )}
                          {message.attachment && (
                            <div className="mt-2 flex items-center gap-2 text-sm opacity-75">
                              <Paperclip size={14} />
                              <span>{message.attachment.name}</span>
                              <span className="text-xs">({(message.attachment.size / 1024).toFixed(1)} KB)</span>
                            </div>
                          )}
                          <p className={`text-xs mt-2 opacity-70 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                            }`}>
                            {formatTime(message.timestamp)}
                          </p>
                          {/* v2.1 — UI payload: job picker, navigate_to button, suggested action chips */}
                          {message.sender === 'david' && message.uiPayload && (
                            <>
                              {/* Job picker — rendered when backend returns highlights.jobs */}
                              {message.uiPayload.highlights?.select_kind === 'job' &&
                                Array.isArray(message.uiPayload.highlights?.jobs) &&
                                message.uiPayload.highlights.jobs.length > 0 && (
                                <div className="mt-3 max-h-64 overflow-y-auto rounded-xl border border-[#D6E0EA] bg-white divide-y divide-[#EDEDED]">
                                  {message.uiPayload.highlights.jobs.map((job: { id: string; title: string; applicant_bucket?: string; applicant_count?: number }) => {
                                    const count = typeof job.applicant_count === 'number' ? job.applicant_count : null;
                                    let label: string | null = null;
                                    if (count !== null) {
                                      label = count === 0 ? null : `${count} applicant${count === 1 ? '' : 's'}`;
                                    } else if (job.applicant_bucket && job.applicant_bucket !== 'none') {
                                      label = 'has applicants';
                                    }
                                    return (
                                      <button
                                        key={`${message.id}-job-${job.id}`}
                                        onClick={() => handleJobSelect(job.id, job.title)}
                                        disabled={isLoading || isCompleted}
                                        className="w-full text-left px-3 py-2 text-sm text-[#015FA6] hover:bg-[#E8EFF6] transition-colors disabled:opacity-50 flex items-center justify-between gap-3"
                                      >
                                        <span className="truncate">{job.title}</span>
                                        {label && (
                                          <span className="text-xs text-[#757575] whitespace-nowrap">{label}</span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {/* Candidate cards — Slice 3 + match-presentation refactor.
                                  Rank-based pill badges and intra-shortlist comparison bars; raw
                                  match_score is intentionally not displayed (it's an unbounded
                                  weighted blend, not a percentage). See utils/candidateMatchPresentation. */}
                              {message.uiPayload.highlights?.select_kind === 'candidate' &&
                                Array.isArray(message.uiPayload.highlights?.candidates) && (() => {
                                  const candidates = message.uiPayload.highlights.candidates as Array<{
                                    candidate_id: string;
                                    name?: string;
                                    email?: string;
                                    match_score?: number;
                                    top_3_skills?: string[];
                                    years_experience?: number;
                                    stage?: string;
                                  }>;
                                  const showBars = shouldShowBars(candidates);
                                  return (
                                    <div className="mt-3 space-y-2">
                                      {candidates.map((cand, rank) => {
                                        const badge = badgeForRank(rank);
                                        const barWidth = barWidthPercent(cand, candidates);
                                        const skills = (cand.top_3_skills || []).join(' · ');
                                        const years = typeof cand.years_experience === 'number' && cand.years_experience > 0
                                          ? `${cand.years_experience} yrs`
                                          : null;
                                        const stageLabel = cand.stage ? cand.stage.charAt(0).toUpperCase() + cand.stage.slice(1) : null;
                                        const badgeClasses = badge === 'Top match'
                                          ? 'bg-[#015FA6] text-white'
                                          : badge === 'Good match'
                                            ? 'bg-[#E8EFF6] text-[#015FA6]'
                                            : 'bg-gray-100 text-gray-600';
                                        return (
                                          <button
                                            key={`${message.id}-cand-${cand.candidate_id}`}
                                            onClick={() => handleCandidateSelect(cand.candidate_id, cand.name || 'this candidate')}
                                            disabled={isLoading || isCompleted}
                                            className="w-full text-left rounded-xl border border-[#D6E0EA] bg-white px-4 py-3 hover:bg-[#E8EFF6] transition-colors disabled:opacity-50"
                                          >
                                            <div className="flex items-start justify-between gap-3">
                                              <div className="min-w-0 flex-1">
                                                <div className="font-medium text-sm text-gray-900 truncate">
                                                  {cand.name || 'Applicant'}
                                                </div>
                                                {skills && (
                                                  <div className="text-xs text-gray-600 mt-0.5 truncate">
                                                    {skills}
                                                  </div>
                                                )}
                                                <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                                                  {years && <span>{years}</span>}
                                                  {stageLabel && (
                                                    <span className="px-1.5 py-0.5 rounded bg-[#E8EFF6] text-[#015FA6] text-[10px] font-medium uppercase tracking-wide">
                                                      {stageLabel}
                                                    </span>
                                                  )}
                                                  {cand.email && <span className="truncate">{cand.email}</span>}
                                                </div>
                                              </div>
                                              <div className="shrink-0">
                                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${badgeClasses}`}>
                                                  {badge}
                                                </span>
                                              </div>
                                            </div>
                                            {barWidth !== null && (
                                              <div className="mt-2 h-1 w-full rounded-full bg-[#EDEDED] overflow-hidden">
                                                <div
                                                  className="h-full bg-[#015FA6]"
                                                  style={{ width: `${barWidth}%` }}
                                                />
                                              </div>
                                            )}
                                          </button>
                                        );
                                      })}
                                      {showBars && (
                                        <div className="text-[10px] text-gray-500 px-1">
                                          Bars compare candidates within this shortlist.
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              <div className="mt-3 flex flex-wrap gap-2">
                                {message.uiPayload.navigate_to && (
                                  <button
                                    onClick={() => navigate(message.uiPayload!.navigate_to!)}
                                    className="px-3 py-1.5 bg-[#015FA6] text-white text-xs font-medium rounded-full hover:bg-[#014a85] transition-colors inline-flex items-center gap-1.5"
                                  >
                                    Open page <span aria-hidden="true">→</span>
                                  </button>
                                )}
                                {message.uiPayload.suggested_actions?.map((action, idx) => (
                                  <button
                                    key={`${message.id}-suggest-${idx}`}
                                    onClick={() => handleSuggestedAction(action.label, action.intent)}
                                    disabled={isLoading || isCompleted}
                                    className="px-3 py-1.5 bg-[#F5F7FA] text-[#015FA6] text-xs font-medium rounded-full border border-[#D6E0EA] hover:bg-[#E8EFF6] transition-colors disabled:opacity-50"
                                  >
                                    {action.label}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />

                {/* Loading/Thinking Indicator */}
                {isLoading && !showProjectSelection && (
                  <div className="flex justify-start">
                    <div className="max-w-2xl bg-white text-gray-900 rounded-3xl rounded-bl-md px-6 py-4 border border-[#EDEDED] shadow-sm">
                      <div className="flex items-center gap-3">
                        {/* Animated spinner */}
                        <div className="relative w-5 h-5">
                          <div className="absolute inset-0 rounded-full border-2 border-[#E8F4FC]"></div>
                          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#015FA6] animate-spin"></div>
                        </div>
                        <span className="text-sm text-[#757575]">David is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Project Selection UI - shown inline in chat */}
                {showProjectSelection && (
                  <div className="flex justify-start">
                    <div className="max-w-2xl bg-white text-gray-900 rounded-3xl rounded-bl-md px-6 py-4 border border-[#EDEDED] shadow-sm">
                      <p className="text-sm text-[#757575] mb-4">Select a folder for your job posting:</p>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto mb-4">
                        {isLoadingProjects ? (
                          <div className="text-center py-4 text-gray-500">Loading folders...</div>
                        ) : projects.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">No folders yet. Create one below.</div>
                        ) : (
                          projects.map((project) => (
                            <button
                              key={project._id || project.projectId}
                              onClick={() => handleProjectSelect(project.projectId || project._id)}
                              className="w-full flex items-center gap-3 p-3 rounded-lg border border-[#EDEDED] hover:border-[#015FA6] hover:bg-blue-50 transition-colors text-left"
                            >
                              <Folder size={20} className="text-[#015FA6]" />
                              <div>
                                <p className="font-medium text-[#0B111E]">{project.name}</p>
                                <p className="text-xs text-[#757575]">{project.jobIds?.length || 0} jobs</p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      <button
                        onClick={() => setShowCreateProjectModal(true)}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-[#A5A5A5] hover:border-[#015FA6] hover:bg-blue-50 transition-colors text-[#757575] hover:text-[#015FA6]"
                      >
                        <Plus size={18} />
                        <span>Create new folder</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Review Job Card - shown after job posting is complete */}
                {isCompleted && completedJobId && (
                  <div className="flex justify-start">
                    <div className="max-w-2xl bg-white rounded-3xl rounded-bl-md px-6 py-5 border border-[#EDEDED] shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-[#F0FFF4] flex items-center justify-center shrink-0">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="9" stroke="#16A34A" strokeWidth="2" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-[#0B111E] text-base">Job Posted Successfully!</p>
                          <p className="text-sm text-[#757575]">Review your posting to make sure everything looks perfect.</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(COMPANY_ROUTES.JOB_DETAILS(completedJobId), { state: { projectId } })}
                        className="w-full px-4 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors flex items-center justify-center gap-2"
                      >
                        Review Your Posted Job
                        <ArrowUpRight size={16} />
                      </button>
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* Input Area */}
            <div className="px-8 py-6 bg-[#F8F9FB]">
              <div className="max-w-4xl mx-auto">
                {/* Slice 4.9 — Read-only banner when viewing a past chat. */}
                {isReadOnly && (
                  <div className="mb-3 px-4 py-3 bg-[#FFF8E5] border border-[#F0DFA0] rounded-xl flex items-center justify-between gap-4">
                    <div className="text-sm text-[#5C4A12]">
                      <span className="font-medium">Read-only.</span> You're viewing a past chat. Click below to start a new conversation.
                    </div>
                    <button
                      onClick={startNewChat}
                      className="shrink-0 px-3 py-1.5 bg-[#015FA6] text-white text-xs font-medium rounded-full hover:bg-[#014a85] transition-colors"
                    >
                      Start new chat
                    </button>
                  </div>
                )}
                <div className="relative bg-white rounded-2xl border border-[#EDEDED] shadow-sm p-4 flex flex-col">
                  <textarea
                    ref={textareaRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isReadOnly ? "Read-only — click Start new chat to reply" : "Type your message here..."}
                    disabled={isLoading || isCompleted || isReadOnly}
                    className="w-full min-h-[60px] resize-none outline-none text-base placeholder-[#A5A5A5] text-[#0B111E] disabled:opacity-50"
                  />

                  {selectedFile && (
                    <div className="flex items-center gap-2 mt-2 p-2 bg-blue-50 rounded-lg text-sm text-[#015FA6]">
                      <Paperclip size={16} />
                      <span>{selectedFile.name}</span>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="ml-auto text-gray-500 hover:text-red-500"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  <div className="flex justify-between items-center mt-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading || isCompleted || isReadOnly}
                      className="text-[#A5A5A5] hover:text-[#015FA6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Paperclip size={20} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt"
                    />
                    <div className="flex items-center gap-4">
                      <button
                        onClick={handleSendMessage}
                        disabled={(inputMessage.trim() === '' && !selectedFile) || isLoading || isCompleted || isReadOnly}
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${(inputMessage.trim() || selectedFile) && !isLoading && !isCompleted && !isReadOnly
                          ? 'bg-[#015FA6] hover:bg-[#014a85]'
                          : 'bg-[#A5A5A5]'
                          }`}
                      >
                        <Send size={18} className="-ml-0.5 mt-0.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&display=swap');

          .font-manrope {
            font-family: 'Manrope', sans-serif;
          }

          @keyframes fade-in {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-in {
            animation: fade-in 0.4s ease-out forwards;
          }

          /* Custom Markdown styles for David's messages */
          .prose {
            color: #0B111E;
          }
          .prose h1, .prose h2, .prose h3, .prose h4 {
            margin-top: 1em;
            margin-bottom: 0.5em;
            font-weight: 700;
          }
          .prose h3 {
            font-size: 1.1em;
          }
          .prose p {
            margin-bottom: 0.75em;
            line-height: 1.6;
          }
          .prose ul, .prose ol {
            margin: 0.5em 0;
            padding-left: 1.5em;
          }
          .prose li {
            margin: 0.25em 0;
          }
          .prose strong {
            font-weight: 700;
            color: #015FA6;
          }
          .prose code {
            background: #F4F7FE;
            color: #015FA6;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 0.9em;
            font-family: 'Monaco', 'Courier New', monospace;
          }
          .prose blockquote {
            border-left: 3px solid #015FA6;
            background: #F4F7FE;
            padding: 0.5em 1em;
            margin: 0.75em 0;
            font-style: italic;
          }
          .prose a {
            color: #015FA6;
            text-decoration: underline;
          }
          .prose a:hover {
            color: #014a85;
          }
        `}</style>

        {/* Create Project Modal */}
        {showCreateProjectModal && (
          <div className="fixed inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-xl font-semibold text-[#0B111E] mb-2">
                Create New Folder
              </h3>
              <p className="text-sm text-[#757575] mb-6">
                Create folders to group similar roles or teams
              </p>
              <input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCreateProject()}
                placeholder="Enter folder name..."
                autoFocus
                className="w-full px-4 py-4 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base mb-6"
              />
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateProjectModal(false);
                    setNewProjectName('');
                  }}
                  className="flex-1 px-6 py-3 border border-[#A5A5A5] text-[#0B111E] font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim() || isCreatingProject}
                  className="flex-1 px-6 py-3 bg-[#015FA6] text-white font-semibold rounded-lg hover:bg-[#014578] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingProject ? 'Creating...' : 'Create Folder'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CompanyLayout>
  );
};

export default TalkToDavid;
